import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import MapView from '../components/MapView';
import { Heart, Bed, Bath, Square, Calendar, Car, ArrowLeft, Clock, Video, Loader2 } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import PropertyImageGallery from '../components/PropertyImageGallery';
import PropertyContactForm from '../components/PropertyContactForm';
import WalkScoreCard from '../components/WalkScoreCard';
import SchoolsSection from '../components/SchoolsSection';
import NeighborhoodInfo from '../components/NeighborhoodInfo';
import AgentCard from '../components/AgentCard';
import TourRequestForm from '../components/TourRequestForm';
import CreditSimulator from '../components/CreditSimulator';
import SEOHead from '../components/seo/SEOHead';
import { useLocalizedNavigation } from '../hooks/useLocalizedNavigation';
import { useProperty } from '../hooks/useSanityData';
import PropertyFactsAndFeatures from '../components/PropertyFactsAndFeatures';

const PropertyDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, toggleSaveProperty, isPropertySaved } = useAuth();
  const { getTextInCurrentLanguage, currentLanguage } = useLanguage();
  const { getLocalizedPath } = useLocalizedNavigation();

  const { property, agent, loading } = useProperty(id || '');

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('property.notFound')}</h2>
          <Button onClick={() => navigate(getLocalizedPath('/properties'))} className="bg-blue-600 hover:bg-blue-700">
            {t('property.backToProperties')}
          </Button>
        </div>
      </div>
    );
  }

  const isSaved = isPropertySaved(property.id);

  const formatPrice = (price: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const handleSaveToggle = () => {
    if (!user) {
      navigate(getLocalizedPath('/login'));
      return;
    }
    toggleSaveProperty(property.id);
    toast({
      title: isSaved ? t('property.removedFromSaved') : t('property.savedSuccessfully'),
      description: isSaved ? t('property.removedFromList') : t('property.addedToList')
    });
  };

  // Function to convert YouTube URL to embed URL
  const getYouTubeEmbedUrl = (url: string): string => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);

    if (match && match[2].length === 11) {
      return `https://www.youtube.com/embed/${match[2]}`;
    }

    // If it's already an embed URL, return as is
    if (url.includes('youtube.com/embed')) {
      return url;
    }

    // Return original URL if it doesn't match YouTube patterns
    return url;
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <SEOHead
        title={`${getTextInCurrentLanguage(property.title)} | Prime Haus`}
        description={getTextInCurrentLanguage(property.description).substring(0, 160)}
        keywords={[property.propertyType, property.city, property.state, 'real estate', 'property for sale']}
        locale={currentLanguage}
        path={`/propiedad/${property.id}`}
        ogImage={property.images[0]}
        type="article"
        propertyData={{
          ...property,
          slug: property.id,
          name: getTextInCurrentLanguage(property.title),
          description: getTextInCurrentLanguage(property.description)
        }}
        breadcrumbs={[
          { name: t('navigation.home'), url: getLocalizedPath('/') },
          { name: t('navigation.properties'), url: getLocalizedPath('/properties') },
          { name: getTextInCurrentLanguage(property.title), url: getLocalizedPath(`/property/${property.id}`) }
        ]}
      />

      {/* Back Button */}
      <div className="bg-white border-b border-gray-200 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button
            onClick={() => navigate(getLocalizedPath('/properties'))}
            variant="ghost"
            className="text-gray-700 hover:text-blue-600"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('property.backToResults')}
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Images and Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Sold Banner */}
            {property.sold && (
              <div className="bg-red-600 text-white text-center py-3 font-bold text-lg uppercase tracking-wide rounded-xl">
                {t('common.sold')}
              </div>
            )}

            {/* Image Gallery */}
            <PropertyImageGallery images={property.images} title={getTextInCurrentLanguage(property.title)} />

            {/* YouTube Video Player */}
            {property.youtubeVideoUrls && property.youtubeVideoUrls.length > 0 && (
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {property.youtubeVideoUrls.length > 1 ? t('property.videoTours') : t('property.videoTour')}
                </h2>
                <div className="space-y-4">
                  {property.youtubeVideoUrls.slice(0, 1).map((videoUrl, index) => (
                    <div key={index} className="aspect-video w-full overflow-hidden rounded-lg">
                      <iframe
                        width="100%"
                        height="100%"
                        src={getYouTubeEmbedUrl(videoUrl)}
                        title={`YouTube video player ${index + 1}`}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      ></iframe>
                    </div>
                  ))}
                  {property.youtubeVideoUrls.length > 1 && (
                    <div className="mt-4">
                      <h3 className="font-medium text-gray-700 mb-2">{t('property.additionalVideos')}</h3>
                      <div className="flex flex-wrap gap-2">
                        {property.youtubeVideoUrls.slice(1).map((videoUrl, index) => (
                          <a
                            key={index}
                            href={videoUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium hover:bg-red-200 transition-colors"
                          >
                            {t('property.video')} {index + 2}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Property Info */}
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h1 className="text-4xl font-bold text-gray-900 mb-2">
                    {formatPrice(property.price)}
                  </h1>
                  <p className="text-lg text-gray-600">
                    {property.address}, {property.city}, {property.state} {property.zipCode}
                  </p>
                </div>
                <Button
                  onClick={handleSaveToggle}
                  variant="outline"
                  size="icon"
                  className={`rounded-full ${
                    isSaved ? 'text-red-500 border-red-500' : 'text-gray-600'
                  }`}
                >
                  <Heart className={`h-6 w-6 ${isSaved ? 'fill-current' : ''}`} />
                </Button>
              </div>

              {/* Quick Stats */}
              <div className="flex flex-wrap gap-4 mb-6 pb-6 border-b border-gray-200">
                <div className="flex items-center gap-2 text-gray-600">
                  <Clock className="h-5 w-5" />
                  <span className="text-sm">
                    {property.daysOnMarket} {property.daysOnMarket === 1 ? t('common.day') : t('common.days')} {t('common.onMarket')}
                  </span>
                </div>
                {property.virtualTourUrl && (
                  <div className="flex items-center gap-2 text-blue-600">
                    <Video className="h-5 w-5" />
                    <a href={property.virtualTourUrl} target="_blank" rel="noopener noreferrer" className="text-sm font-medium hover:underline">
                      {t('property.virtualTourAvailable')}
                    </a>
                  </div>
                )}
                {property.youtubeVideoUrls && property.youtubeVideoUrls.length > 0 && (
                  <div className="flex items-center gap-2 text-red-600">
                    <Video className="h-5 w-5" />
                    <a href={property.youtubeVideoUrls[0]} target="_blank" rel="noopener noreferrer" className="text-sm font-medium hover:underline">
                      {t('property.youtubeVideoTour')}
                    </a>
                  </div>
                )}
                {property.openHouseDate && (
                  <div className="flex items-center gap-2 text-green-600">
                    <Calendar className="h-5 w-5" />
                    <span className="text-sm font-medium">
                      {t('common.openHouse')}: {new Date(property.openHouseDate).toLocaleDateString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                )}
              </div>

              {/* Key Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 pb-6 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <Bed className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">{property.beds}</div>
                    <div className="text-sm text-gray-600">{t('common.bedrooms')}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <Bath className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">{property.baths}</div>
                    <div className="text-sm text-gray-600">{t('common.bathrooms')}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <Square className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">{property.sqft.toLocaleString()}</div>
                    <div className="text-sm text-gray-600">{t('common.sqft')}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <Car className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">{property.parking}</div>
                    <div className="text-sm text-gray-600">{t('common.parking')}</div>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('property.aboutThisHome')}</h2>
                <p className="text-gray-700 leading-relaxed">{getTextInCurrentLanguage(property.description)}</p>
              </div>

              {/* Facts & Features */}
              <PropertyFactsAndFeatures property={property} />
            </div>

            {/* Neighborhood Data */}
            <WalkScoreCard
              walkScore={property.neighborhoodData.walkScore}
              transitScore={property.neighborhoodData.transitScore}
              bikeScore={property.neighborhoodData.bikeScore}
            />

            <NeighborhoodInfo data={property.neighborhoodData} />

            {/* Schools */}
            <SchoolsSection schools={property.neighborhoodData.schools} />

            {/* Map */}
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('property.location')}</h2>
              <div className="h-[400px]">
                <MapView properties={[property]} center={[property.latitude, property.longitude]} zoom={13} />
              </div>
            </div>

            {/* Credit Simulator */}
            <CreditSimulator propertyPrice={property.price} />
          </div>

          {/* Right Column - Agent & Contact */}
          <div className="lg:col-span-1 space-y-6">
            {/* Agent Card */}
            {agent && <AgentCard agent={agent} />}

            {/* Tour Request Form */}
            <TourRequestForm
              propertyId={property.id}
              agentId={property.agentId}
              propertyTitle={getTextInCurrentLanguage(property.title)}
            />

            {/* Contact Form */}
            <PropertyContactForm />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetailPage;
